import { create } from 'zustand';
import axios from 'axios';
import { persist } from 'zustand/middleware';

export const useAuthStore = create(
  persist(
    (set) => ({
  user: null,
  isLoading: false,
  error: null,
  sessionExpiresAt: null,

  // Check if user is already logged in (via token in localStorage)
  checkAuth: async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      set({ user: null, isLoading: false });
      return;
    }
    
    // Check if session is expired
    const expiresAt = localStorage.getItem('sessionExpiresAt');
    if (expiresAt && new Date(expiresAt) < new Date()) {
      localStorage.removeItem('token');
      localStorage.removeItem('sessionExpiresAt');
      set({ 
        user: null, 
        isLoading: false, 
        error: 'Сессия истекла, пожалуйста, войдите снова',
        sessionExpiresAt: null
      });
      return;
    }

    try {
      set({ isLoading: true });
      const config = {
        headers: {
          Authorization: `Bearer ${token}`
        }
      };
      const { data } = await axios.get('/api/auth/profile', config);
      set({ user: data, isLoading: false, error: null });
    } catch (error) {
      localStorage.removeItem('token');
      set({ user: null, isLoading: false, error: error.response?.data?.message || 'Failed to authenticate' });
    }
  },

  // Login user
  login: async (email, password) => {
    try {
      set({ isLoading: true });
      const { data } = await axios.post('/api/auth/login', { email, password });
      localStorage.setItem('token', data.token);
      localStorage.setItem('sessionExpiresAt', data.expiresAt);
      set({ 
        user: data, 
        isLoading: false, 
        error: null,
        sessionExpiresAt: data.expiresAt
      });
      return true;
    } catch (error) {
      set({ 
        isLoading: false, 
        error: error.response?.data?.message || 'Invalid email or password' 
      });
      return false;
    }
  },

  // Check registration data and send verification code
  registerCheck: async (userData) => {
    try {
      set({ isLoading: true });
      const { data } = await axios.post('/api/auth/register-check', userData);
      set({ 
        isLoading: false,
        error: null
      });
      return data;
    } catch (error) {
      set({
        error: error.response?.data?.message || 'Проверка регистрационных данных не удалась',
        isLoading: false
      });
      throw error;
    }
  },

  // Register user with verification code
  register: async (userData) => {
    try {
      set({ isLoading: true });
      const { data } = await axios.post('/api/auth/register', userData);
      localStorage.setItem('token', data.token);
      localStorage.setItem('sessionExpiresAt', data.expiresAt);
      set({ 
        user: data, // Сохраняем все данные пользователя, а не только data.user
        isLoading: false,
        error: null,
        sessionExpiresAt: data.expiresAt
      });
      return data;
    } catch (error) {
      set({
        error: error.response?.data?.message || 'Регистрация не удалась',
        isLoading: false
      });
      throw error;
    }
  },
  
  // Verify email with code
  verifyEmail: async (code) => {
    try {
      set({ isLoading: true });
      const token = localStorage.getItem('token');
      if (!token) {
        set({ isLoading: false, error: 'Not authenticated' });
        return false;
      }
      
      const config = {
        headers: {
          Authorization: `Bearer ${token}`
        }
      };
      
      const { data } = await axios.post('/api/auth/verify-email', { code }, config);
      
      // Update user state with verified email status
      set(state => ({
        user: { ...state.user, isEmailVerified: true },
        isLoading: false,
        error: null
      }));
      
      return true;
    } catch (error) {
      set({ 
        isLoading: false, 
        error: error.response?.data?.message || 'Email verification failed' 
      });
      return false;
    }
  },
  
  // Resend verification code
  resendVerification: async () => {
    try {
      set({ isLoading: true });
      const token = localStorage.getItem('token');
      if (!token) {
        set({ isLoading: false, error: 'Not authenticated' });
        return false;
      }
      
      const config = {
        headers: {
          Authorization: `Bearer ${token}`
        }
      };
      
      await axios.post('/api/auth/resend-verification', {}, config);
      
      set({ isLoading: false, error: null });
      return true;
    } catch (error) {
      set({ 
        isLoading: false, 
        error: error.response?.data?.message || 'Failed to resend verification code' 
      });
      return false;
    }
  },

  // Logout user
  logout: async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const config = {
          headers: {
            Authorization: `Bearer ${token}`
          }
        };
        await axios.post('/api/auth/logout', {}, config);
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('sessionExpiresAt');
      set({ user: null, error: null, sessionExpiresAt: null });
    }
  },

  // Update user profile
  updateProfile: async (userData) => {
    try {
      set({ isLoading: true, error: null });
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          Authorization: `Bearer ${token}`
        }
      };
      const { data } = await axios.put('/api/auth/profile', userData, config);
      
      // Update user data and token
      localStorage.setItem('token', data.token);
      localStorage.setItem('sessionExpiresAt', data.expiresAt);
      
      set({ 
        user: data, 
        isLoading: false, 
        error: null,
        sessionExpiresAt: data.expiresAt
      });
      
      return true;
    } catch (error) {
      set({ 
        isLoading: false, 
        error: error.response?.data?.message || 'Не удалось обновить профиль' 
      });
      return false;
    }
  },
  
  // Send verification code for email change
  sendEmailVerification: async (email) => {
    try {
      set({ isLoading: true, error: null });
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          Authorization: `Bearer ${token}`
        }
      };
      const { data } = await axios.post('/api/auth/verify-email', { email }, config);
      
      set({ 
        isLoading: false,
        error: null
      });
      
      return data;
    } catch (error) {
      set({ 
        isLoading: false, 
        error: error.response?.data?.message || 'Не удалось отправить код подтверждения' 
      });
      return false;
    }
  },
  
  // Change email with verification code
  changeEmail: async (email, code) => {
    try {
      set({ isLoading: true, error: null });
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          Authorization: `Bearer ${token}`
        }
      };
      const { data } = await axios.post('/api/auth/change-email', { email, code }, config);
      
      // Update token and user data
      localStorage.setItem('token', data.token);
      localStorage.setItem('sessionExpiresAt', data.expiresAt);
      
      // Update user data with new email
      set(state => ({ 
        user: { ...state.user, email },
        isLoading: false,
        error: null,
        sessionExpiresAt: data.expiresAt
      }));
      
      return true;
    } catch (error) {
      console.log('Error changing email:', error);
      
      // Проверяем, был ли email успешно изменен на сервере, несмотря на ошибку
      // Это может произойти из-за проблем с созданием новой сессии
      if (error.response?.status === 500) {
        // Обновляем локальные данные пользователя
        set(state => ({ 
          user: { ...state.user, email },
          isLoading: false,
          error: 'Email был изменен, но возникла проблема с обновлением сессии. Пожалуйста, войдите снова.'
        }));
        
        // Возвращаем true, так как email был успешно изменен
        return true;
      }
      
      set({ 
        isLoading: false, 
        error: error.response?.data?.message || 'Не удалось изменить email' 
      });
      return false;
    }
  },
  
  // Change password
  changePassword: async (currentPassword, newPassword) => {
    try {
      set({ isLoading: true, error: null });
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          Authorization: `Bearer ${token}`
        }
      };
      const { data } = await axios.put('/api/auth/change-password', 
        { currentPassword, newPassword }, 
        config
      );
      
      // Update token
      localStorage.setItem('token', data.token);
      localStorage.setItem('sessionExpiresAt', data.expiresAt);
      
      set({ 
        isLoading: false,
        error: null,
        sessionExpiresAt: data.expiresAt
      });
      
      return true;
    } catch (error) {
      set({ 
        isLoading: false, 
        error: error.response?.data?.message || 'Не удалось изменить пароль' 
      });
      return false;
    }
  },

  // Clear error
  clearError: () => set({ error: null }),
  
  // Request password reset
  requestPasswordReset: async (email) => {
    try {
      set({ isLoading: true });
      const { data } = await axios.post('/api/auth/reset-password-request', { email });
      set({ 
        isLoading: false,
        error: null
      });
      return { success: true, message: data.message };
    } catch (error) {
      set({
        error: error.response?.data?.message || 'Не удалось отправить запрос на восстановление пароля',
        isLoading: false
      });
      return { success: false, message: error.response?.data?.message };
    }
  },
  
  // Reset password with temporary password and set new password
  resetPassword: async (email, tempPassword, newPassword) => {
    try {
      set({ isLoading: true });
      const { data } = await axios.post('/api/auth/reset-password', { 
        email, 
        tempPassword, 
        newPassword 
      });
      localStorage.setItem('token', data.token);
      localStorage.setItem('sessionExpiresAt', data.expiresAt);
      set({ 
        user: data,
        isLoading: false, 
        error: null,
        sessionExpiresAt: data.expiresAt
      });
      return true;
    } catch (error) {
      set({
        error: error.response?.data?.message || 'Не удалось сбросить пароль',
        isLoading: false
      });
      return false;
    }
  },
  
  // Get active sessions
  getSessions: async () => {
    try {
      set({ isLoading: true });
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          Authorization: `Bearer ${token}`
        }
      };
      const { data } = await axios.get('/api/auth/sessions', config);
      set({ isLoading: false, error: null });
      return data;
    } catch (error) {
      set({ 
        isLoading: false, 
        error: error.response?.data?.message || 'Failed to get sessions' 
      });
      return [];
    }
  },
  
  // Invalidate a specific session
  invalidateSession: async (sessionId) => {
    try {
      set({ isLoading: true });
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          Authorization: `Bearer ${token}`
        }
      };
      await axios.delete(`/api/auth/sessions/${sessionId}`, config);
      set({ isLoading: false, error: null });
      return true;
    } catch (error) {
      set({ 
        isLoading: false, 
        error: error.response?.data?.message || 'Failed to invalidate session' 
      });
      return false;
    }
  },
  
  // Invalidate all other sessions
  invalidateAllSessions: async () => {
    try {
      set({ isLoading: true });
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          Authorization: `Bearer ${token}`
        }
      };
      await axios.delete('/api/auth/sessions', config);
      set({ isLoading: false, error: null });
      return true;
    } catch (error) {
      set({ 
        isLoading: false, 
        error: error.response?.data?.message || 'Failed to invalidate sessions' 
      });
      return false;
    }
  }
}),
{
  name: 'auth-storage',
  partialize: (state) => ({ 
    user: state.user,
    sessionExpiresAt: state.sessionExpiresAt
  })
}
));
